// Vendor Follow-up — dispatch_admin's chase board. Who hasn't shipped
// (dispatched but no tracking number), who said delayed, which OOS
// conversations are open, and how responsive each vendor has been lately.
// Every number opens its order list (shared drill modal) and exports CSV.

import { useEffect, useState } from 'react';
import { vendorFollowupApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { Loader2, Megaphone, RefreshCcw } from 'lucide-react';

function Tile({ label, value, info, alert, onClick }) {
  return (
    <div className={`rounded-xl border p-4 ${alert ? 'border-rose-300 bg-rose-50/60' : 'border-emerald-200 bg-emerald-50/40'}`}>
      <div className="flex items-center justify-between gap-1">
        <button onClick={onClick} title="See which orders these are"
                className="text-[11px] uppercase tracking-wide text-blue-600 hover:underline text-left">
          {label}
        </button>
        <InfoTip title={label}>{info}</InfoTip>
      </div>
      <button onClick={onClick} title="See which orders these are"
              className={`text-3xl font-semibold tabular-nums hover:underline ${alert ? 'text-rose-700' : 'text-gray-900'}`}>
        {value}
      </button>
    </div>
  );
}

function BucketCell({ count, tone, onClick }) {
  if (count === 0) return <td className="px-3 py-2 text-right text-gray-300">—</td>;
  const tones = {
    ok: 'text-gray-700',
    warn: 'text-amber-700 font-medium',
    alarm: 'text-rose-700 font-semibold',
  };
  return (
    <td className="px-3 py-2 text-right">
      <button onClick={onClick} title="See which orders these are"
              className={`tabular-nums hover:underline ${tones[tone]}`}>
        {count}
      </button>
    </td>
  );
}

export default function VendorFollowup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  function load() {
    setLoading(true);
    vendorFollowupApi.build()
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-blue-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Vendor Follow-up</h1>
          <InfoTip title="What this board is for" align="left">
            The daily chase list. <b>No tracking number</b> = we emailed the
            dispatch file but the vendor hasn't shipped (or hasn't shared the
            number) — the longer it sits, the more likely the customer
            complains. <b>Delayed</b> and <b>OOS pending</b> are open vendor
            conversations. The responsiveness table shows which vendors are
            habitually slow. Every number opens the exact orders, and every
            list exports to CSV so you can send it to the vendor as-is.
          </InfoTip>
        </div>
        <button onClick={load} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Who to chase today. Empty is good.
      </p>

      {loading && !data && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Checking…
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <Tile label="Dispatched, no tracking #"
                  value={data.noTn.total}
                  alert={data.noTn.total > 0}
                  onClick={() => openDrill('no_tn')}
                  info="Parcels whose dispatch file was emailed to the vendor but no tracking number has been recorded yet. 0-2 days is normal turnaround; 3-5 days deserves a reminder; 6+ days means the parcel probably hasn't shipped — chase the vendor today." />
            <Tile label="Vendor said delayed"
                  value={data.delayed.total}
                  alert={data.delayed.total > 0}
                  onClick={() => openDrill('vendor_delayed_open')}
                  info="Orders where the vendor told us dispatch will be late and which are still waiting. Each one is a promise to follow up on — and if one vendor appears here often, that's a pattern worth raising." />
            <Tile label="OOS — customer answer pending"
                  value={data.oosPending.total}
                  alert={data.oosPending.total > 0}
                  onClick={() => openDrill('oos_pending')}
                  info="The vendor reported out-of-stock (or can't supply) and offered an alternative or nothing — and the customer conversation hasn't concluded. These age fast: the customer is waiting on us, not the vendor." />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              No tracking number — by vendor and age{' '}
              <InfoTip title="Reading this table">
                Each vendor's unshipped parcels bucketed by how long ago we
                emailed the dispatch file. <b>0-2d</b> is normal, <b>3-5d</b>
                send a reminder, <b>6+d</b> chase hard — and "oldest" shows
                their worst case. Click any count for the exact orders with
                batch numbers and dates; export and forward it to the vendor.
              </InfoTip>
            </div>
            {data.noTn.byVendor.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Every dispatched parcel has a tracking number. Clean.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Vendor</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">0-2d</th>
                    <th className="text-right px-3 py-2">3-5d</th>
                    <th className="text-right px-3 py-2">6+d</th>
                    <th className="text-right px-3 py-2">Oldest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.noTn.byVendor.map((v) => (
                    <tr key={v.vendor_id || v.vendor_name}>
                      <td className="px-3 py-2 font-medium">{v.vendor_name || '(unknown vendor)'}</td>
                      <BucketCell count={v.total} tone="warn"
                                  onClick={() => openDrill('no_tn', { vendorId: v.vendor_id })} />
                      <BucketCell count={v.b0_2} tone="ok"
                                  onClick={() => openDrill('no_tn', { vendorId: v.vendor_id, bucket: 'b0_2' })} />
                      <BucketCell count={v.b3_5} tone="warn"
                                  onClick={() => openDrill('no_tn', { vendorId: v.vendor_id, bucket: 'b3_5' })} />
                      <BucketCell count={v.b6p} tone="alarm"
                                  onClick={() => openDrill('no_tn', { vendorId: v.vendor_id, bucket: 'b6p' })} />
                      <td className={`px-3 py-2 text-right text-xs tabular-nums ${v.oldest_days > 5 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {v.oldest_days}d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Vendor responsiveness — last 30 days{' '}
              <InfoTip title="Reading this table">
                How quickly each vendor turns a dispatch email into a tracking
                number. <b>Avg days → TN</b> above 3 is slow (red).{' '}
                <b>Within 3d</b> is the share of parcels shipped on time.
                <b> Open no-TN</b> is their current unshipped backlog — click
                it to see (and export) the list. Use this in vendor
                conversations: it's their scorecard.
              </InfoTip>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Vendor</th>
                  <th className="text-right px-3 py-2">Parcels</th>
                  <th className="text-right px-3 py-2">TN received</th>
                  <th className="text-right px-3 py-2">Avg days → TN</th>
                  <th className="text-right px-3 py-2">Within 3d</th>
                  <th className="text-right px-3 py-2">Open no-TN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.responsiveness.map((v) => (
                  <tr key={v.vendor_id || v.vendor_name}>
                    <td className="px-3 py-2 font-medium">{v.vendor_name || '(unknown vendor)'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{v.shipments_30d}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{v.with_tn}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${v.avg_days_to_tn != null && v.avg_days_to_tn > 3 ? 'text-rose-700 font-semibold' : ''}`}>
                      {v.avg_days_to_tn != null ? `${v.avg_days_to_tn}d` : '—'}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums ${v.pct_within_3d != null && v.pct_within_3d < 80 ? 'text-amber-700 font-medium' : ''}`}>
                      {v.pct_within_3d != null ? `${v.pct_within_3d}%` : '—'}
                    </td>
                    <BucketCell count={v.no_tn_open} tone="alarm"
                                onClick={() => openDrill('no_tn', { vendorId: v.vendor_id })} />
                  </tr>
                ))}
                {data.responsiveness.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                    No dispatches in the last 30 days.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
